/**
 * CreateJobForm Component
 *
 * Form for creating a new job with validation
 * Supports both Flow 1 (with agent selection) and Flow 2 (implicit agent creation)
 */

import React, { useState } from 'react';
import { JobCreate } from '../../types/job';

interface CreateJobFormProps {
  projectId: string;
  batchId: string;
  agents?: Array<{ agent_id: string; domain: string }>;
  showAgentSelection?: boolean;
  onSubmit: (data: JobCreate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CreateJobForm: React.FC<CreateJobFormProps> = ({
  projectId,
  batchId,
  agents = [],
  showAgentSelection = false,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    goal: '',
    website_url: '',
    input_id: '',
    agent_id: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string) => {
    let error = '';

    switch (name) {
      case 'goal':
        if (!value.trim()) {
          error = 'Goal is required';
        } else if (value.length > 5000) {
          error = 'Goal must be 5000 characters or less';
        }
        break;
      case 'website_url':
        if (!value.trim()) {
          error = 'Website URL is required';
        } else {
          try {
            new URL(value);
          } catch {
            error = 'Must be a valid URL';
          }
        }
        break;
      case 'agent_id':
        if (showAgentSelection && !value) {
          error = 'Agent selection is required';
        }
        break;
    }

    return error;
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, formData[field as keyof typeof formData]);
    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Mark all relevant fields as touched
    const fieldsToValidate = showAgentSelection
      ? ['goal', 'website_url', 'agent_id']
      : ['goal', 'website_url'];

    const newTouched: Record<string, boolean> = {};
    const newErrors: Record<string, string> = {};

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      const error = validateField(field, formData[field as keyof typeof formData]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    onSubmit({
      goal: formData.goal.trim(),
      website_url: formData.website_url.trim(),
      input_id: formData.input_id.trim() || undefined,
      agent_id: showAgentSelection && formData.agent_id ? formData.agent_id : undefined,
      project_id: projectId,
      batch_id: batchId,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Job</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Goal Field */}
          <div>
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-1">
              Goal <span className="text-red-500">*</span>
            </label>
            <textarea
              id="goal"
              value={formData.goal}
              onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
              onBlur={() => handleBlur('goal')}
              disabled={isLoading}
              rows={4}
              className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                touched.goal && errors.goal
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="Describe what the agent should accomplish..."
              aria-invalid={touched.goal && !!errors.goal}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.goal.length} / 5000 characters
            </p>
            {touched.goal && errors.goal && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {errors.goal}
              </p>
            )}
          </div>

          {/* Website URL Field */}
          <div>
            <label
              htmlFor="website_url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="website_url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              onBlur={() => handleBlur('website_url')}
              disabled={isLoading}
              className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                touched.website_url && errors.website_url
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder="https://example.com"
              aria-invalid={touched.website_url && !!errors.website_url}
            />
            {touched.website_url && errors.website_url && (
              <p className="mt-1 text-sm text-red-600" role="alert">
                {errors.website_url}
              </p>
            )}
          </div>

          {/* Input ID Field (Optional) */}
          <div>
            <label htmlFor="input_id" className="block text-sm font-medium text-gray-700 mb-1">
              Input ID <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              type="text"
              id="input_id"
              value={formData.input_id}
              onChange={(e) => setFormData({ ...formData, input_id: e.target.value })}
              disabled={isLoading}
              className="block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., TEST-001"
            />
            <p className="mt-1 text-xs text-gray-500">
              Identifier for associating with evaluation data
            </p>
          </div>

          {/* Agent Selection (Flow 1 only) */}
          {showAgentSelection && (
            <div>
              <label
                htmlFor="agent_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Agent <span className="text-red-500">*</span>
              </label>
              <select
                id="agent_id"
                value={formData.agent_id}
                onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
                onBlur={() => handleBlur('agent_id')}
                disabled={isLoading}
                className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                  touched.agent_id && errors.agent_id
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                aria-invalid={touched.agent_id && !!errors.agent_id}
              >
                <option value="">Select an agent...</option>
                {agents.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.domain}
                  </option>
                ))}
              </select>
              {touched.agent_id && errors.agent_id && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.agent_id}
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              Create Job
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
